import { Badge } from "@qw/ui";

interface ContractAgreementDetailsProps {
  contract: {
    agreementType?: string | null;
    contractStatus?: string | null;
    producerCode?: string | null;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    marketType?: string | null;
    hasBindingAuthority?: boolean | null;
    bindingAuthorityLimit?: number | null;
    aggregateLimit?: number | null;
    maxPolicyTermMonths?: number | null;
    premiumFinanceAllowed?: boolean | null;
    premiumGuarantee?: string | null;
    paymentTerms?: string | null;
    terminationNoticeDays?: number | null;
    defaultCurePeriodDays?: number | null;
    eoMinimumCoverage?: number | null;
    eoRatingRequirement?: string | null;
    fidelityBondRequired?: boolean | null;
    dataBreachNotifyHours?: number | null;
    recordRetentionYears?: number | null;
    diligentSearchRequired?: boolean | null;
    subProducerAllowed?: boolean | null;
    changeOfControlPct?: number | null;
    governingLawState?: string | null;
    disputeMethod?: string | null;
    arbitrationVenue?: string | null;
    authorizedStates?: string[];
  };
}

const agreementTypeLabels: Record<string, string> = {
  producer: "Producer",
  brokerage: "Brokerage",
  general_agency: "General Agency",
  binding_authority: "Binding Authority",
};

const marketTypeLabels: Record<string, string> = {
  admitted: "Admitted",
  surplus: "Surplus",
  both: "Both",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  expired: "Expired",
  terminated: "Terminated",
};

const disputeMethodLabels: Record<string, string> = {
  arbitration: "Arbitration",
  litigation: "Litigation",
  either: "Either",
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function BooleanDisplay({ value, label }: { value: boolean | null | undefined; label: string }) {
  if (value == null) {
    return null;
  }
  return (
    <div className="text-sm flex py-1 w-full gap-x-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value ? "Yes" : "No"}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null) {
    return null;
  }
  return (
    <div className="text-sm flex w-full py-1 gap-x-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export function ContractAgreementDetails({ contract }: ContractAgreementDetailsProps) {
  const hasMetadata =
    contract.agreementType ||
    contract.contractStatus ||
    contract.producerCode ||
    contract.effectiveDate ||
    contract.marketType;
  const hasAuthority =
    contract.hasBindingAuthority != null ||
    contract.bindingAuthorityLimit ||
    contract.aggregateLimit ||
    contract.maxPolicyTermMonths;
  const hasFinancial =
    contract.paymentTerms || contract.terminationNoticeDays != null || contract.defaultCurePeriodDays != null;
  const hasCompliance =
    contract.eoMinimumCoverage != null ||
    contract.fidelityBondRequired != null ||
    contract.dataBreachNotifyHours != null ||
    contract.recordRetentionYears != null;
  const hasDispute = contract.governingLawState || contract.disputeMethod;
  const hasStates = contract.authorizedStates && contract.authorizedStates.length > 0;

  if (!hasMetadata && !hasAuthority && !hasFinancial && !hasCompliance && !hasDispute && !hasStates) {
    return null;
  }

  return (
    <div className="space-y-2">
      {hasMetadata && (
        <>
          <div className="flex flex-wrap gap-2">
            <div className="flex flex-wrap gap-x-2 gap-y-1">
              {contract.agreementType && (
                <Badge variant="outline">{agreementTypeLabels[contract.agreementType] ?? contract.agreementType}</Badge>
              )}
              {contract.contractStatus && (
                <Badge variant={contract.contractStatus === "active" ? "default" : "secondary"}>
                  {statusLabels[contract.contractStatus] ?? contract.contractStatus}
                </Badge>
              )}
              {contract.marketType && (
                <Badge variant="outline">{marketTypeLabels[contract.marketType] ?? contract.marketType}</Badge>
              )}
            </div>
            {hasStates && (
              <>
                {contract.authorizedStates!.map((state) => (
                  <Badge key={state} variant="secondary" className="font-normal">
                    {state}
                  </Badge>
                ))}
              </>
            )}
          </div>

          <DetailRow label="Producer Code" value={contract.producerCode} />
          {contract.effectiveDate && <DetailRow label="Effective" value={formatDate(contract.effectiveDate)} />}
          {contract.expirationDate && <DetailRow label="Expires" value={formatDate(contract.expirationDate)} />}
        </>
      )}

      {hasAuthority && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <BooleanDisplay value={contract.hasBindingAuthority} label="Binding Authority" />
          {contract.bindingAuthorityLimit != null && (
            <DetailRow label="Binding Limit" value={formatCurrency(contract.bindingAuthorityLimit)} />
          )}
          {contract.aggregateLimit != null && (
            <DetailRow label="Aggregate Limit" value={formatCurrency(contract.aggregateLimit)} />
          )}
          {contract.maxPolicyTermMonths != null && (
            <DetailRow label="Max Term" value={`${contract.maxPolicyTermMonths} months`} />
          )}
          <BooleanDisplay value={contract.premiumFinanceAllowed} label="Premium Finance" />
          <DetailRow label="Premium Guarantee" value={contract.premiumGuarantee} />
        </div>
      )}

      {hasFinancial && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <DetailRow label="Payment Terms" value={contract.paymentTerms} />
          {contract.terminationNoticeDays != null && (
            <DetailRow label="Termination Notice" value={`${contract.terminationNoticeDays} days`} />
          )}
          {contract.defaultCurePeriodDays != null && (
            <DetailRow label="Cure Period" value={`${contract.defaultCurePeriodDays} days`} />
          )}
        </div>
      )}

      {hasCompliance && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {contract.eoMinimumCoverage != null && (
            <DetailRow label="E&O Minimum" value={formatCurrency(contract.eoMinimumCoverage)} />
          )}
          <DetailRow label="E&O Rating" value={contract.eoRatingRequirement} />
          <BooleanDisplay value={contract.fidelityBondRequired} label="Fidelity Bond" />
          {contract.dataBreachNotifyHours != null && (
            <DetailRow label="Data Breach Notice" value={`${contract.dataBreachNotifyHours} hours`} />
          )}
          {contract.recordRetentionYears != null && (
            <DetailRow label="Record Retention" value={`${contract.recordRetentionYears} years`} />
          )}
          <BooleanDisplay value={contract.diligentSearchRequired} label="Diligent Search" />
          <BooleanDisplay value={contract.subProducerAllowed} label="Sub-Producers" />
          {contract.changeOfControlPct != null && (
            <DetailRow label="Change of Control" value={`${contract.changeOfControlPct}%`} />
          )}
        </div>
      )}

      {hasDispute && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <DetailRow label="Governing Law" value={contract.governingLawState} />
          {contract.disputeMethod && (
            <DetailRow label="Disputes" value={disputeMethodLabels[contract.disputeMethod] ?? contract.disputeMethod} />
          )}
          <DetailRow label="Arbitration Venue" value={contract.arbitrationVenue} />
        </div>
      )}
    </div>
  );
}
